import * as THREE from 'three';
import type { ImportedBodyEntry } from '../loaders/glb';
import type { SelectionInteractionMeta } from '../render/selection-extension';
import { ImportedModelObjectMetadataService } from '../metadata';
import {
    resolveImportedModelTopologyRepository,
    isImportedModelTopologyHost,
} from '../topology/ImportedModelTopologyRepository';
import type { ImportedMeshTopologyBinding } from '../topology/ImportedMeshTopologyBinding';
import {
    areImportedBodyBindingIdsEquivalent,
    normalizeImportedBodyBindingId,
} from '../utils';
import { ImportedModelSelectionObjectResolver } from './ImportedModelSelectionObjectResolver';
import { isImportedTopologySelectionObject, resolveImportedTopologyHost } from './ImportedModelTopologyPickResolver';

export interface ImportedModelSelectionSummaryObject {
    id: string;
    type: string;
    name: string;
    metadata: Record<string, unknown>;
}

export interface ImportedModelSelectionSummary {
    selectionObjectId: string;
    bodyBindingId?: string;
    selectionObject: THREE.Object3D | null;
    sourceObject: THREE.Object3D | null;
    selectedObject: ImportedModelSelectionSummaryObject;
}

/**
 * 解析导入拓扑代理用于 selection proxy 回收的稳定源对象。
 *
 * 设计说明：
 * 1. 拓扑代理命中的是 FeatureFace / FeatureLine / FeaturePoint，本身不应成为设计树语义对象；
 * 2. metadata 迁移后，pick host 会继承 visible mesh 的导入语义，因此优先使用 host 自身做稳定解析；
 * 3. 若 host 尚未写入 metadata，则回退到当前拓扑层级的 parent 链，避免摘要停在临时代理层。
 *
 * @param sourceObject 当前命中的拓扑代理对象。
 * @returns 用于解析最终导入语义对象的源对象。
 */
function resolveImportedTopologySelectionResolveSource(sourceObject: THREE.Object3D): THREE.Object3D {
    const topologyHost = resolveImportedTopologyHost(sourceObject);
    if (topologyHost && ImportedModelObjectMetadataService.peekMetadata(topologyHost)) {
        return topologyHost;
    }
    return topologyHost?.parent ?? sourceObject.parent ?? sourceObject;
}

/**
 * 从拓扑代理或 visible mesh 反查 imported body 运行时记录。
 *
 * @param sourceObject 当前命中的场景对象。
 * @returns imported body 记录；无法唯一定位时返回 null。
 */
function resolveImportedBodyRecord(
    sourceObject: THREE.Object3D,
    selectionObject?: THREE.Object3D | null,
): ImportedMeshTopologyBinding | null {
    const topologyHost = resolveImportedTopologyHost(sourceObject);
    const repository = resolveImportedModelTopologyRepository(topologyHost ?? sourceObject)
        ?? resolveImportedModelTopologyRepository(selectionObject);
    if (!repository) {
        return null;
    }

    if (topologyHost && isImportedModelTopologyHost(topologyHost)) {
        return repository.resolveBodyRecordFromPickHost(topologyHost);
    }

    if (sourceObject instanceof THREE.Mesh) {
        const bodyRecords = repository.resolveBodyRecordsByVisibleMesh(sourceObject);
        return bodyRecords.length === 1 ? bodyRecords[0] ?? null : null;
    }

    return null;
}

/**
 * 解析当前选择应携带的 imported bodyBindingId。
 *
 * @param sourceObject 当前命中的场景对象。
 * @param interactionMeta 交互语义元数据。
 * @returns 稳定 bodyBindingId；缺失时返回空字符串。
 */
function resolveSelectionBodyBindingId(
    sourceObject: THREE.Object3D,
    interactionMeta: SelectionInteractionMeta | undefined,
    selectionObject?: THREE.Object3D | null,
): string {
    const explicitBodyBindingId = normalizeImportedBodyBindingId(interactionMeta?.bodyBindingId);
    if (explicitBodyBindingId) {
        return explicitBodyBindingId;
    }

    // .puppy 特征实体重新导入后，用户可能直接命中 visible mesh，也可能命中
    // FeatureFace / FeatureLine / FeaturePoint 拾取代理；两条路径都应该回收为同一个
    // bodyBindingId，避免设计树节点和场景高亮脱节。多实体共用 mesh 时
    // resolveImportedBodyRecord 会返回 null，不会把不唯一的 mesh 误判为 body。
    return resolveImportedBodyRecord(sourceObject, selectionObject)?.bodyBindingId ?? '';
}

/**
 * 从 hierarchy bodyEntries 中读取实体显示名。
 *
 * @param bodyEntries 当前 hierarchy 中记录的实体条目。
 * @param bodyBindingId 目标 bodyBindingId。
 * @returns 实体显示名；缺失时返回空字符串。
 */
function resolveBodyEntryLabel(
    bodyEntries: ImportedBodyEntry[] | undefined,
    bodyBindingId: string,
): string {
    if (!Array.isArray(bodyEntries) || !bodyBindingId) {
        return '';
    }

    return bodyEntries.find((entry) => (
        areImportedBodyBindingIdsEquivalent(entry.bodyId, bodyBindingId)
    ))?.label?.trim() ?? '';
}

/**
 * 构建导入模型选择摘要。
 *
 * 设计说明：
 * 1. 摘要构建放在 cad-core，宿主层只消费结构化结果，不再自行探测 legacy glbHierarchy/userData；
 * 2. 这里统一收口 selection proxy、显示名称、路径与实体摘要，避免 web-client 散落重复逻辑；
 * 3. body 语义优先由 interactionMeta 决定，其余类型以 GLB 层级语义为准。
 *
 * @param scene 当前场景。
 * @param objectId 选中对象 ID。
 * @param interactionMeta 交互语义元数据。
 * @returns 导入模型选择摘要；未命中导入模型时返回 null。
 */
export function buildImportedModelSelectionSummary(
    scene: THREE.Scene,
    objectId: string,
    interactionMeta?: SelectionInteractionMeta,
): ImportedModelSelectionSummary | null {
    const selectionObjectResolver = new ImportedModelSelectionObjectResolver({ scene });
    const sourceObject = findSceneObjectById(scene, objectId);
    if (!sourceObject || !selectionObjectResolver.isImportedModelObject(sourceObject)) {
        return null;
    }

    //------ 导入拓扑代理摘要语义回收 开始------
    /**
     * 视口里直接命中的 imported `FeatureFace / FeatureLine / FeaturePoint`
     * 只是拓扑代理，不应直接作为设计树语义对象回流。
     *
     * 说明：
     * 1. 对拓扑代理，应先回退到所属 visible mesh / imported 语义宿主；
     * 2. 再复用 selection proxy 规则解析最终树语义对象；
     * 3. 这样“选中一个面/边/点 -> 同步右侧导入树”时，树能定位到零件或实体节点，而不是代理对象本身。
     */
    const selectionResolveSource = isImportedTopologySelectionObject(sourceObject)
        ? resolveImportedTopologySelectionResolveSource(sourceObject)
        : sourceObject;
    const selectionObject = selectionObjectResolver.resolveSelectionObject(selectionResolveSource) ?? selectionResolveSource;
    //------ 导入拓扑代理摘要语义回收 结束------
    const hierarchyRecord = selectionObject
        ? ImportedModelObjectMetadataService.resolveHierarchy(selectionObject)
            ?? ImportedModelObjectMetadataService.resolveHierarchy(sourceObject)
        : ImportedModelObjectMetadataService.resolveHierarchy(sourceObject);

    const selectionObjectId = selectionObject?.uuid ?? sourceObject.uuid;
    const bodyBindingId = resolveSelectionBodyBindingId(sourceObject, interactionMeta, selectionObject);
    const isFeatureTreeBodyEntitySelection = interactionMeta?.source === 'feature-tree'
        && interactionMeta.semantic === 'entity'
        && interactionMeta.glbNodeRole === 'body';
    const semanticType = isFeatureTreeBodyEntitySelection
        ? 'body'
        : hierarchyRecord?.meta?.semanticKind
            ?? 'glb-node';
    const hierarchyDisplayName = hierarchyRecord?.name
        ?? selectionObject?.name
        ?? sourceObject.name
        ?? selectionObjectId;
    const bodyDisplayName = semanticType === 'body'
        ? interactionMeta?.displayName?.trim()
            || resolveBodyEntryLabel(hierarchyRecord?.meta?.bodyEntries, bodyBindingId)
            || hierarchyDisplayName
        : '';
    const displayName = semanticType === 'body'
        ? bodyDisplayName
        : hierarchyDisplayName;
    const selectionTarget = ImportedModelObjectMetadataService.resolveSelectionTargetFromSelfOrAncestors(sourceObject)
        ?? (selectionObject
            ? ImportedModelObjectMetadataService.resolveSelectionTargetFromSelfOrAncestors(selectionObject)
            : undefined);
    const assemblyNodeId = selectionTarget?.assemblyNodeId
        ?? ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(sourceObject)
        ?? (selectionObject
            ? ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(selectionObject)
            : undefined);

    const metadata: Record<string, unknown> = {
        name: displayName,
        fileId: (selectionObject ? ImportedModelObjectMetadataService.resolveFileId(selectionObject) : undefined)
            ?? ImportedModelObjectMetadataService.resolveFileId(sourceObject)
            ?? hierarchyRecord?.fileId
            ?? '',
        path: hierarchyRecord?.path ?? '',
        nodeType: hierarchyRecord?.nodeType ?? selectionObject?.type ?? sourceObject.type,
        externalFileUuid: hierarchyRecord?.meta?.externalFileUuid ?? '',
        externalAssemblyName: hierarchyRecord?.meta?.externalAssemblyName ?? '',
        selectable: Boolean(hierarchyRecord?.selectable),
        selectionObjectId,
        sourceObjectId: sourceObject.uuid,
        source: 'imported-model',
    };
    if (assemblyNodeId) {
        metadata.assemblyNodeId = assemblyNodeId;
    }

    if (semanticType === 'body') {
        metadata.type = 'Solid Body';
        metadata.createdByFeatureId = bodyBindingId || selectionObjectId;
        if (bodyBindingId) {
            metadata.bodyBindingId = bodyBindingId;
        }
    }

    return {
        selectionObjectId,
        ...(bodyBindingId ? { bodyBindingId } : {}),
        selectionObject,
        sourceObject,
        selectedObject: {
            id: selectionObjectId,
            type: semanticType,
            name: displayName,
            metadata,
        },
    };
}

/**
 * 根据对象 ID 查找场景对象。
 * @param scene 当前场景。
 * @param objectId 目标对象 ID。
 * @returns 命中的场景对象；未命中时返回 null。
 */
function findSceneObjectById(scene: THREE.Scene, objectId: string): THREE.Object3D | null {
    let targetObject: THREE.Object3D | null = null;
    scene.traverse((object) => {
        if (!targetObject && object.uuid === objectId) {
            targetObject = object;
        }
    });
    return targetObject;
}

